import React from "react";
import { AUTH_PREFIX_PATH, APP_PREFIX_PATH } from "configs/AppConfig";

export const publicRoutes = [
  {
    key: "login",
    path: `/login`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/login")
    ),
  },
  // Contract Signing (public - no auth required)
  {
    key: "contract-sign",
    path: `/contract/sign/:token`,
    component: React.lazy(() => import("views/app-views/codeforce/contracts/ContractSign")),
  },
  // Public Payment Page (no auth required)
  {
    key: "public-payment",
    path: `/pay/:token`,
    component: React.lazy(() => import("views/app-views/codeforce/paymentsuite/PublicPaymentPage")),
  },
  // Public Onboarding Page (no auth required)
  {
    key: "public-onboarding",
    path: `/onboard/:token`,
    component: React.lazy(() => import("views/app-views/codeforce/paymentsuite/PublicOnboardingPage")),
  },
  {
    key: "forgot-password",
    path: `${AUTH_PREFIX_PATH}/forgot-password`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/forgot-password")
    ),
  },
  {
    key: "error-page-1",
    path: `${AUTH_PREFIX_PATH}/error-page-1`,
    component: React.lazy(() => import("views/auth-views/errors/error-page-1")),
  },
  {
    key: "error-page-2",
    path: `${AUTH_PREFIX_PATH}/error-page-2`,
    component: React.lazy(() => import("views/auth-views/errors/error-page-2")),
  },
  // Portal routes (public shell, auth handled internally)
  {
    key: "portal-login",
    path: `/portal/login`,
    component: React.lazy(() => import("views/portal-views/login")),
  },
  {
    key: "portal-verify",
    path: `/portal/verify/:token`,
    component: React.lazy(() => import("views/portal-views/verify")),
  },
];

export const protectedRoutes = [
  // ================== CODEFORCE CRM ROUTES ==================
  // Dashboard
  {
    key: "codeforce-dashboard",
    path: `${APP_PREFIX_PATH}/codeforce/dashboard`,
    component: React.lazy(() => import("views/app-views/codeforce/dashboard")),
  },
  
  // Clients
  {
    key: "codeforce-clients",
    path: `${APP_PREFIX_PATH}/codeforce/clients`,
    component: React.lazy(() => import("views/app-views/codeforce/clients")),
  },
  {
    key: "codeforce-clients-add",
    path: `${APP_PREFIX_PATH}/codeforce/clients/add`,
    component: React.lazy(() => import("views/app-views/codeforce/clients/AddClient")),
  },
  {
    key: "codeforce-clients-edit",
    path: `${APP_PREFIX_PATH}/codeforce/clients/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/clients/AddClient")),
  },
  {
    key: "codeforce-clients-detail",
    path: `${APP_PREFIX_PATH}/codeforce/clients/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/clients/ClientDetail")),
  },
  
  // Projects
  {
    key: "codeforce-projects",
    path: `${APP_PREFIX_PATH}/codeforce/projects`,
    component: React.lazy(() => import("views/app-views/codeforce/projects")),
  },
  {
    key: "codeforce-projects-add",
    path: `${APP_PREFIX_PATH}/codeforce/projects/add`,
    component: React.lazy(() => import("views/app-views/codeforce/projects/AddProject")),
  },
  {
    key: "codeforce-projects-edit",
    path: `${APP_PREFIX_PATH}/codeforce/projects/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/projects/AddProject")),
  },
  {
    key: "codeforce-projects-detail",
    path: `${APP_PREFIX_PATH}/codeforce/projects/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/projects/ProjectDetail")),
  },
  
  // Payments
  {
    key: "codeforce-payments",
    path: `${APP_PREFIX_PATH}/codeforce/payments`,
    component: React.lazy(() => import("views/app-views/codeforce/payments")),
  },
  {
    key: "codeforce-payments-add",
    path: `${APP_PREFIX_PATH}/codeforce/payments/add`,
    component: React.lazy(() => import("views/app-views/codeforce/payments/AddPayment")),
  },
  {
    key: "codeforce-payments-edit",
    path: `${APP_PREFIX_PATH}/codeforce/payments/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/payments/AddPayment")),
  },
  
  // Contracts
  {
    key: "codeforce-contracts",
    path: `${APP_PREFIX_PATH}/codeforce/contracts`,
    component: React.lazy(() => import("views/app-views/codeforce/contracts")),
  },
  {
    key: "codeforce-contracts-add",
    path: `${APP_PREFIX_PATH}/codeforce/contracts/add`,
    component: React.lazy(() => import("views/app-views/codeforce/contracts/AddContract")),
  },
  {
    key: "codeforce-contracts-edit",
    path: `${APP_PREFIX_PATH}/codeforce/contracts/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/contracts/AddContract")),
  },
  
  // Servers
  {
    key: "codeforce-servers",
    path: `${APP_PREFIX_PATH}/codeforce/servers`,
    component: React.lazy(() => import("views/app-views/codeforce/servers")),
  },
  
  // Company Settings
  {
    key: "codeforce-settings",
    path: `${APP_PREFIX_PATH}/codeforce/settings`,
    component: React.lazy(() => import("views/app-views/codeforce/settings")),
  },
  
  // CMS
  {
    key: "codeforce-cms",
    path: `${APP_PREFIX_PATH}/codeforce/cms`,
    component: React.lazy(() => import("views/app-views/codeforce/cms")),
  },
  {
    key: "codeforce-cms-add",
    path: `${APP_PREFIX_PATH}/codeforce/cms/add`,
    component: React.lazy(() => import("views/app-views/codeforce/cms/AddContent")),
  },
  {
    key: "codeforce-cms-edit",
    path: `${APP_PREFIX_PATH}/codeforce/cms/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/cms/AddContent")),
  },

  // Tasks
  {
    key: "codeforce-tasks",
    path: `${APP_PREFIX_PATH}/codeforce/tasks`,
    component: React.lazy(() => import("views/app-views/codeforce/tasks")),
  },
  {
    key: "codeforce-tasks-add",
    path: `${APP_PREFIX_PATH}/codeforce/tasks/add`,
    component: React.lazy(() => import("views/app-views/codeforce/tasks/AddTask")),
  },
  {
    key: "codeforce-tasks-edit",
    path: `${APP_PREFIX_PATH}/codeforce/tasks/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/tasks/AddTask")),
  },
  {
    key: "codeforce-tasks-detail",
    path: `${APP_PREFIX_PATH}/codeforce/tasks/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/tasks/AddTask")),
  },

  // Estimates
  {
    key: "codeforce-estimates",
    path: `${APP_PREFIX_PATH}/codeforce/estimates`,
    component: React.lazy(() => import("views/app-views/codeforce/estimates")),
  },
  {
    key: "codeforce-estimates-add",
    path: `${APP_PREFIX_PATH}/codeforce/estimates/add`,
    component: React.lazy(() => import("views/app-views/codeforce/estimates/AddEstimate")),
  },
  {
    key: "codeforce-estimates-edit",
    path: `${APP_PREFIX_PATH}/codeforce/estimates/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/estimates/AddEstimate")),
  },
  {
    key: "codeforce-estimates-detail",
    path: `${APP_PREFIX_PATH}/codeforce/estimates/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/estimates/AddEstimate")),
  },

  // Invoices
  {
    key: "codeforce-invoices",
    path: `${APP_PREFIX_PATH}/codeforce/invoices`,
    component: React.lazy(() => import("views/app-views/codeforce/invoices")),
  },
  {
    key: "codeforce-invoices-add",
    path: `${APP_PREFIX_PATH}/codeforce/invoices/add`,
    component: React.lazy(() => import("views/app-views/codeforce/invoices/AddInvoice")),
  },
  {
    key: "codeforce-invoices-edit",
    path: `${APP_PREFIX_PATH}/codeforce/invoices/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/invoices/AddInvoice")),
  },
  {
    key: "codeforce-invoices-detail",
    path: `${APP_PREFIX_PATH}/codeforce/invoices/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/invoices/AddInvoice")),
  },

  // Notes
  {
    key: "codeforce-notes",
    path: `${APP_PREFIX_PATH}/codeforce/notes`,
    component: React.lazy(() => import("views/app-views/codeforce/notes")),
  },
  {
    key: "codeforce-notes-add",
    path: `${APP_PREFIX_PATH}/codeforce/notes/add`,
    component: React.lazy(() => import("views/app-views/codeforce/notes/AddNote")),
  },
  {
    key: "codeforce-notes-edit",
    path: `${APP_PREFIX_PATH}/codeforce/notes/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/notes/AddNote")),
  },
  {
    key: "codeforce-notes-detail",
    path: `${APP_PREFIX_PATH}/codeforce/notes/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/notes/AddNote")),
  },

  // Reports
  {
    key: "codeforce-reports",
    path: `${APP_PREFIX_PATH}/codeforce/reports`,
    component: React.lazy(() => import("views/app-views/codeforce/reports")),
  },

  // Payment Suite
  {
    key: "codeforce-payment-suite",
    path: `${APP_PREFIX_PATH}/codeforce/payment-suite`,
    component: React.lazy(() => import("views/app-views/codeforce/paymentsuite")),
  },

  // Payouts
  {
    key: "codeforce-payouts",
    path: `${APP_PREFIX_PATH}/codeforce/payouts`,
    component: React.lazy(() => import("views/app-views/codeforce/payouts")),
  },

  // Expenses
  {
    key: "codeforce-expenses",
    path: `${APP_PREFIX_PATH}/codeforce/expenses`,
    component: React.lazy(() => import("views/app-views/codeforce/expenses")),
  },
  {
    key: "codeforce-expenses-add",
    path: `${APP_PREFIX_PATH}/codeforce/expenses/add`,
    component: React.lazy(() => import("views/app-views/codeforce/expenses/AddExpense")),
  },
  {
    key: "codeforce-expenses-edit",
    path: `${APP_PREFIX_PATH}/codeforce/expenses/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/expenses/AddExpense")),
  },

  // Pre-Leads
  {
    key: "codeforce-preleads",
    path: `${APP_PREFIX_PATH}/codeforce/pre-leads`,
    component: React.lazy(() => import("views/app-views/codeforce/preleads")),
  },

  // Leads
  {
    key: "codeforce-leads",
    path: `${APP_PREFIX_PATH}/codeforce/leads`,
    component: React.lazy(() => import("views/app-views/codeforce/leads")),
  },
  {
    key: "codeforce-leads-add",
    path: `${APP_PREFIX_PATH}/codeforce/leads/add`,
    component: React.lazy(() => import("views/app-views/codeforce/leads/AddLead")),
  },
  {
    key: "codeforce-leads-edit",
    path: `${APP_PREFIX_PATH}/codeforce/leads/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/leads/AddLead")),
  },
  {
    key: "codeforce-leads-detail",
    path: `${APP_PREFIX_PATH}/codeforce/leads/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/leads/LeadDetail")),
  },

  // Employees
  {
    key: "codeforce-employees",
    path: `${APP_PREFIX_PATH}/codeforce/employees`,
    component: React.lazy(() => import("views/app-views/codeforce/employees")),
  },
  {
    key: "codeforce-employees-add",
    path: `${APP_PREFIX_PATH}/codeforce/employees/add`,
    component: React.lazy(() => import("views/app-views/codeforce/employees/AddEmployee")),
  },
  {
    key: "codeforce-employees-edit",
    path: `${APP_PREFIX_PATH}/codeforce/employees/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/employees/AddEmployee")),
  },
  {
    key: "codeforce-employees-detail",
    path: `${APP_PREFIX_PATH}/codeforce/employees/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/employees/EmployeeDetail")),
  },

  // Users
  {
    key: "codeforce-users",
    path: `${APP_PREFIX_PATH}/codeforce/users`,
    component: React.lazy(() => import("views/app-views/codeforce/users")),
  },
  {
    key: "codeforce-users-add",
    path: `${APP_PREFIX_PATH}/codeforce/users/add`,
    component: React.lazy(() => import("views/app-views/codeforce/users/AddUser")),
  },
  {
    key: "codeforce-users-edit",
    path: `${APP_PREFIX_PATH}/codeforce/users/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/users/AddUser")),
  },

  // Pay Stubs
  {
    key: "codeforce-paystubs",
    path: `${APP_PREFIX_PATH}/codeforce/paystubs`,
    component: React.lazy(() => import("views/app-views/codeforce/paystubs")),
  },
  {
    key: "codeforce-paystubs-add",
    path: `${APP_PREFIX_PATH}/codeforce/paystubs/add`,
    component: React.lazy(() => import("views/app-views/codeforce/paystubs/AddPayStub")),
  },
  {
    key: "codeforce-paystubs-edit",
    path: `${APP_PREFIX_PATH}/codeforce/paystubs/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/paystubs/AddPayStub")),
  },

  // Workflows
  {
    key: "codeforce-workflows",
    path: `${APP_PREFIX_PATH}/codeforce/workflows`,
    component: React.lazy(() => import("views/app-views/codeforce/workflows")),
  },
  {
    key: "codeforce-workflows-add",
    path: `${APP_PREFIX_PATH}/codeforce/workflows/add`,
    component: React.lazy(() => import("views/app-views/codeforce/workflows/AddWorkflow")),
  },
  {
    key: "codeforce-workflows-edit",
    path: `${APP_PREFIX_PATH}/codeforce/workflows/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/workflows/AddWorkflow")),
  },
  {
    key: "codeforce-workflows-history",
    path: `${APP_PREFIX_PATH}/codeforce/workflows/history`,
    component: React.lazy(() => import("views/app-views/codeforce/workflows/WorkflowHistory")),
  },
  {
    key: "codeforce-workflows-history-id",
    path: `${APP_PREFIX_PATH}/codeforce/workflows/:id/history`,
    component: React.lazy(() => import("views/app-views/codeforce/workflows/WorkflowHistory")),
  },

  // Email Campaigns
  {
    key: "codeforce-campaigns",
    path: `${APP_PREFIX_PATH}/codeforce/campaigns`,
    component: React.lazy(() => import("views/app-views/codeforce/campaigns")),
  },
  {
    key: "codeforce-campaigns-add",
    path: `${APP_PREFIX_PATH}/codeforce/campaigns/add`,
    component: React.lazy(() => import("views/app-views/codeforce/campaigns/AddCampaign")),
  },
  {
    key: "codeforce-campaigns-edit",
    path: `${APP_PREFIX_PATH}/codeforce/campaigns/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/campaigns/AddCampaign")),
  },
  {
    key: "codeforce-campaigns-stats",
    path: `${APP_PREFIX_PATH}/codeforce/campaigns/:id/stats`,
    component: React.lazy(() => import("views/app-views/codeforce/campaigns/CampaignStats")),
  },

  // Chat
  {
    key: "codeforce-chat",
    path: `${APP_PREFIX_PATH}/codeforce/chat`,
    component: React.lazy(() => import("views/app-views/codeforce/chat")),
  },

  // Mail
  {
    key: "codeforce-mail-detail",
    path: `${APP_PREFIX_PATH}/codeforce/mail/:folder/:uid`,
    component: React.lazy(() => import("views/app-views/codeforce/mail")),
  },
  {
    key: "codeforce-mail-folder",
    path: `${APP_PREFIX_PATH}/codeforce/mail/:folder`,
    component: React.lazy(() => import("views/app-views/codeforce/mail")),
  },
  {
    key: "codeforce-mail",
    path: `${APP_PREFIX_PATH}/codeforce/mail`,
    component: React.lazy(() => import("views/app-views/codeforce/mail")),
  },

  // Corporate Books (CRA catch-up & ongoing bookkeeping)
  {
    key: "codeforce-corporate-books",
    path: `${APP_PREFIX_PATH}/codeforce/corporate-books`,
    component: React.lazy(() => import("views/app-views/codeforce/corporate-books")),
  },

  // Workspace Editor (VS Code-inspired notes / code IDE)
  {
    key: "codeforce-workspace",
    path: `${APP_PREFIX_PATH}/codeforce/workspace`,
    component: React.lazy(() => import("views/app-views/codeforce/workspace")),
  },
  {
    key: "codeforce-workspace-settings",
    path: `${APP_PREFIX_PATH}/codeforce/workspace/settings`,
    component: React.lazy(() => import("views/app-views/codeforce/workspace/Settings")),
  },
  {
    key: "codeforce-workspace-editor",
    path: `${APP_PREFIX_PATH}/codeforce/workspace/editor`,
    component: React.lazy(() => import("views/app-views/codeforce/workspace/Editor")),
    meta: { blankLayout: true },
  },
  {
    key: "codeforce-workspace-file",
    path: `${APP_PREFIX_PATH}/codeforce/workspace/:workspaceId/file/:fileId`,
    component: React.lazy(() => import("views/app-views/codeforce/workspace/Editor")),
    meta: { blankLayout: true },
  },
  {
    key: "codeforce-workspace-detail",
    path: `${APP_PREFIX_PATH}/codeforce/workspace/:workspaceId`,
    component: React.lazy(() => import("views/app-views/codeforce/workspace/Editor")),
    meta: { blankLayout: true },
  },

  // Accounting
  {
    key: "codeforce-accounting",
    path: `${APP_PREFIX_PATH}/codeforce/accounting`,
    component: React.lazy(() => import("views/app-views/codeforce/accounting")),
  },
  {
    key: "codeforce-accounting-add-account",
    path: `${APP_PREFIX_PATH}/codeforce/accounting/accounts/add`,
    component: React.lazy(() => import("views/app-views/codeforce/accounting/AddAccount")),
  },
  {
    key: "codeforce-accounting-edit-account",
    path: `${APP_PREFIX_PATH}/codeforce/accounting/accounts/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/accounting/AddAccount")),
  },
  {
    key: "codeforce-accounting-ledger",
    path: `${APP_PREFIX_PATH}/codeforce/accounting/accounts/:id/ledger`,
    component: React.lazy(() => import("views/app-views/codeforce/accounting/AccountLedger")),
  },
  {
    key: "codeforce-accounting-journal",
    path: `${APP_PREFIX_PATH}/codeforce/accounting/journal`,
    component: React.lazy(() => import("views/app-views/codeforce/accounting/JournalEntries")),
  },
  {
    key: "codeforce-accounting-journal-add",
    path: `${APP_PREFIX_PATH}/codeforce/accounting/journal/add`,
    component: React.lazy(() => import("views/app-views/codeforce/accounting/AddJournalEntry")),
  },
  {
    key: "codeforce-accounting-journal-edit",
    path: `${APP_PREFIX_PATH}/codeforce/accounting/journal/edit/:id`,
    component: React.lazy(() => import("views/app-views/codeforce/accounting/AddJournalEntry")),
  },
  {
    key: "codeforce-accounting-profit-loss",
    path: `${APP_PREFIX_PATH}/codeforce/accounting/profit-loss`,
    component: React.lazy(() => import("views/app-views/codeforce/accounting/ProfitLoss")),
  },
  {
    key: "codeforce-accounting-balance-sheet",
    path: `${APP_PREFIX_PATH}/codeforce/accounting/balance-sheet`,
    component: React.lazy(() => import("views/app-views/codeforce/accounting/BalanceSheet")),
  },
  {
    key: "codeforce-accounting-tax-summary",
    path: `${APP_PREFIX_PATH}/codeforce/accounting/tax-summary`,
    component: React.lazy(() => import("views/app-views/codeforce/accounting/TaxSummary")),
  },
];

// Portal protected routes (client users)
export const portalRoutes = [
  {
    key: "portal-dashboard",
    path: `/portal/dashboard`,
    component: React.lazy(() => import("views/portal-views/dashboard")),
  },
  {
    key: "portal-projects",
    path: `/portal/projects`,
    component: React.lazy(() => import("views/portal-views/projects")),
  },
  {
    key: "portal-projects-detail",
    path: `/portal/projects/:id`,
    component: React.lazy(() => import("views/portal-views/projects/ProjectDetail")),
  },
  {
    key: "portal-invoices",
    path: `/portal/invoices`,
    component: React.lazy(() => import("views/portal-views/invoices")),
  },
  {
    key: "portal-invoices-detail",
    path: `/portal/invoices/:id`,
    component: React.lazy(() => import("views/portal-views/invoices/InvoiceDetail")),
  },
  {
    key: "portal-contracts",
    path: `/portal/contracts`,
    component: React.lazy(() => import("views/portal-views/contracts")),
  },
  {
    key: "portal-transactions",
    path: `/portal/transactions`,
    component: React.lazy(() => import("views/portal-views/transactions")),
  },
  {
    key: "portal-subscriptions",
    path: `/portal/subscriptions`,
    component: React.lazy(() => import("views/portal-views/subscriptions")),
  },
  {
    key: "portal-settings",
    path: `/portal/settings`,
    component: React.lazy(() => import("views/portal-views/settings")),
  },
];
